import React, { useEffect, useState } from 'react';
import {
  CreditCard, Loader2, DollarSign, TrendingUp, RefreshCw,
  Undo2, ChevronRight, Search, Wallet,
} from 'lucide-react';
import { useAdminStore } from '../../store/adminStore';

const TYPE_LABELS: Record<string, string> = {
  game_purchase: 'Achat de jeu',
  wallet_topup: 'Recharge portefeuille',
  hosting_subscription: 'Hébergement',
  erozion_plus: 'Erozion Plus',
  refund: 'Remboursement',
};

const STATUS_STYLES: Record<string, { label: string; cls: string }> = {
  completed: { label: 'Complété', cls: 'bg-success/10 text-success' },
  pending: { label: 'En attente', cls: 'bg-warning/10 text-warning' },
  failed: { label: 'Échoué', cls: 'bg-error/10 text-error' },
  refunded: { label: 'Remboursé', cls: 'bg-info/10 text-info' },
};

const AdminBilling: React.FC = () => {
  const {
    transactions, transactionsTotal, revenueStats, isLoadingTransactions,
    fetchTransactions, refundTransaction, creditWallet,
  } = useAdminStore();

  const [typeFilter, setTypeFilter] = useState('');
  const [refundingId, setRefundingId] = useState<string | null>(null);
  const [walletModal, setWalletModal] = useState(false);
  const [walletUserId, setWalletUserId] = useState('');
  const [walletAmount, setWalletAmount] = useState('');
  const [walletReason, setWalletReason] = useState('');

  useEffect(() => { fetchTransactions(0, typeFilter || undefined); }, [typeFilter]);

  const handleRefund = async (txId: string) => {
    setRefundingId(txId);
    await refundTransaction(txId);
    setRefundingId(null);
  };

  const handleCreditWallet = async () => {
    if (!walletUserId || !walletAmount || !walletReason) return;
    const ok = await creditWallet(walletUserId, Number(walletAmount), walletReason);
    if (ok) {
      setWalletModal(false);
      setWalletUserId('');
      setWalletAmount('');
      setWalletReason('');
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-text-primary">Facturation & Transactions</h2>
            <p className="text-xs text-text-muted mt-0.5">Toutes les transactions de la plateforme</p>
          </div>
          <button
            onClick={() => setWalletModal(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-admin-accent/10 text-admin-accent text-sm font-medium hover:bg-admin-accent/20 transition-colors"
          >
            <Wallet className="h-3.5 w-3.5" /> Créditer wallet
          </button>
        </div>

        {/* Revenue cards */}
        {revenueStats && (
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4">
              <div className="flex items-center gap-2 text-xs text-text-muted mb-1">
                <DollarSign className="h-3.5 w-3.5" /> Revenu total
              </div>
              <p className="text-xl font-bold text-success tabular-nums">{Number(revenueStats.total_revenue).toFixed(2)} $</p>
            </div>
            <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4">
              <div className="flex items-center gap-2 text-xs text-text-muted mb-1">
                <Undo2 className="h-3.5 w-3.5" /> Remboursé
              </div>
              <p className="text-xl font-bold text-error tabular-nums">{Number(revenueStats.total_refunded).toFixed(2)} $</p>
            </div>
            <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4">
              <div className="flex items-center gap-2 text-xs text-text-muted mb-1">
                <TrendingUp className="h-3.5 w-3.5" /> Transactions
              </div>
              <p className="text-xl font-bold text-text-primary tabular-nums">{revenueStats.total_transactions}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-1 bg-bg-tertiary/50 rounded-lg p-0.5 w-fit">
          {[
            { value: '', label: 'Tous' },
            { value: 'game_purchase', label: 'Jeux' },
            { value: 'erozion_plus', label: 'Erozion+' },
            { value: 'hosting_subscription', label: 'Hosting' },
            { value: 'wallet_topup', label: 'Wallet' },
          ].map((f) => (
            <button
              key={f.value}
              onClick={() => setTypeFilter(f.value)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                typeFilter === f.value ? 'bg-bg-secondary text-text-primary shadow-sm' : 'text-text-muted hover:text-text-secondary'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Transaction list */}
        {isLoadingTransactions ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-admin-accent" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-16">
            <CreditCard className="h-10 w-10 text-text-muted/20 mx-auto mb-3" />
            <p className="text-sm text-text-muted">Aucune transaction</p>
          </div>
        ) : (
          <>
            <div className="bg-bg-secondary/40 rounded-xl border border-border-color/50 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border-color/30">
                    <th className="text-left text-[10px] font-semibold text-text-muted uppercase tracking-wider px-4 py-2.5">Utilisateur</th>
                    <th className="text-left text-[10px] font-semibold text-text-muted uppercase tracking-wider px-4 py-2.5">Type</th>
                    <th className="text-left text-[10px] font-semibold text-text-muted uppercase tracking-wider px-4 py-2.5">Description</th>
                    <th className="text-right text-[10px] font-semibold text-text-muted uppercase tracking-wider px-4 py-2.5">Montant</th>
                    <th className="text-left text-[10px] font-semibold text-text-muted uppercase tracking-wider px-4 py-2.5">Statut</th>
                    <th className="text-left text-[10px] font-semibold text-text-muted uppercase tracking-wider px-4 py-2.5">Date</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-color/20">
                  {transactions.map((tx) => {
                    const s = STATUS_STYLES[tx.status] || STATUS_STYLES.pending;
                    return (
                      <tr key={tx.id} className="hover:bg-highlight/10 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {tx.avatar_url ? (
                              <img src={tx.avatar_url} className="w-6 h-6 rounded-full object-cover" />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-admin-accent/10 flex items-center justify-center text-[10px] font-bold text-admin-accent">?</div>
                            )}
                            <span className="text-xs text-text-primary truncate max-w-[120px]">{tx.username}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs bg-bg-tertiary px-1.5 py-0.5 rounded text-text-secondary">{TYPE_LABELS[tx.type] || tx.type}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-text-secondary truncate max-w-[180px] block">{tx.item_name || tx.description || '—'}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-semibold text-text-primary tabular-nums">
                            {tx.type === 'refund' ? '+' : ''}{Number(tx.amount).toFixed(2)} $
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${s.cls}`}>{s.label}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-text-muted">{new Date(tx.created_at).toLocaleDateString('fr-CA')}</span>
                        </td>
                        <td className="px-4 py-3">
                          {tx.status === 'completed' && tx.type !== 'refund' && (
                            <button
                              onClick={() => handleRefund(tx.id)}
                              disabled={refundingId === tx.id}
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-error hover:bg-error/10 transition-colors disabled:opacity-50"
                            >
                              {refundingId === tx.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />}
                              Rembourser
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {transactions.length < transactionsTotal && (
              <button
                onClick={() => fetchTransactions(transactions.length, typeFilter || undefined)}
                className="flex items-center gap-1 mx-auto text-sm text-admin-accent hover:text-admin-accent-hover"
              >
                Charger plus <ChevronRight className="h-3.5 w-3.5" />
              </button>
            )}
          </>
        )}

        {/* Wallet credit modal */}
        {walletModal && (
          <div className="fixed inset-0 bg-black/60 z-[300] flex items-center justify-center" onClick={() => setWalletModal(false)}>
            <div className="bg-bg-secondary rounded-xl border border-border-color p-6 w-96 space-y-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-base font-bold text-text-primary flex items-center gap-2"><Wallet className="h-4 w-4 text-admin-accent" /> Créditer un wallet</h3>
              <input
                value={walletUserId}
                onChange={(e) => setWalletUserId(e.target.value)}
                placeholder="ID utilisateur"
                className="w-full px-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary placeholder-text-muted"
              />
              <input
                value={walletAmount}
                onChange={(e) => setWalletAmount(e.target.value)}
                type="number"
                step="0.01"
                min="1"
                max="1000"
                placeholder="Montant ($)"
                className="w-full px-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary placeholder-text-muted"
              />
              <input
                value={walletReason}
                onChange={(e) => setWalletReason(e.target.value)}
                placeholder="Raison"
                className="w-full px-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary placeholder-text-muted"
              />
              <div className="flex gap-2">
                <button onClick={() => setWalletModal(false)} className="flex-1 px-3 py-2 rounded-lg bg-bg-tertiary text-text-secondary text-sm">Annuler</button>
                <button onClick={handleCreditWallet} disabled={!walletUserId || !walletAmount || !walletReason} className="flex-1 px-3 py-2 rounded-lg bg-admin-accent text-white text-sm font-medium disabled:opacity-50">Créditer</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBilling;
